import { useCallback, useState } from 'react';
import { useIntl } from 'react-intl';
import { mapRepliconDateToMidnightUTCString } from '~/modules/common/dates/convert';
import {
  AllocationDistributionMode,
  ResourceAllocationScheduleRulesUpdateBehaviour
} from '~/types';
import { useSnackbar } from '~/modules/common/snackbar';
import { useDialogState } from '~/modules/common/hooks';
import {
  ALLOCATION_PERIOD,
  getHoursValueByPeriod
} from '../../AllocationByPeriod';

export const useSetResourceAllocationOnSubmit = ({
  existingResourceAllocation,
  userUri,
  onClose,
  onUpdateResourceAllocation
}) => {
  const { formatMessage } = useIntl();
  const { showSnackbar } = useSnackbar();

  const {
    open: isSmartFitOpen,
    closeDialog: closeSmartFitDialog,
    openDialog: openSmartFitDialog
  } = useDialogState(false);

  const [smartFitSubmission, setSmartFitSubmission] = useState(null);

  const submitAllocation = useCallback(
    async ({
      dateRange,
      hoursPerPeriod,
      periodType,
      distributionMode = AllocationDistributionMode.FitsAvailableHours
    }) => {
      await onUpdateResourceAllocation({
        allocation: {
          id: existingResourceAllocation.id,
          user: {
            userUri
          },
          allocationHoursInDateRangeInput: {
            dateRange,
            hoursPerPeriod,
            periodType,
            distributionMode
          },
          allocationScheduleRulesUpdateBehaviour:
            ResourceAllocationScheduleRulesUpdateBehaviour.ReplaceOnly
        },
        existingResourceAllocation
      });

      showSnackbar &&
        showSnackbar(
          formatMessage({
            id: 'setResourceAllocationDialog.snackbarSuccessMessage'
          })
        );

      onClose();
    },
    [
      existingResourceAllocation,
      userUri,
      formatMessage,
      onClose,
      onUpdateResourceAllocation,
      showSnackbar
    ]
  );

  const handleSetAllocation = useCallback(
    async (_, distributionMode) => {
      closeSmartFitDialog();
      await submitAllocation({
        dateRange: smartFitSubmission?.allocationDateRange,
        hoursPerPeriod: smartFitSubmission?.totalAllocatedHours,
        periodType: smartFitSubmission?.periodType,
        distributionMode
      });
    },
    [smartFitSubmission, submitAllocation, closeSmartFitDialog]
  );

  const onFormSubmit = useCallback(
    values => {
      const hoursPerPeriod =
        parseFloat(
          getHoursValueByPeriod({
            selectedAllocationPeriod: values.selectedAllocationPeriod,
            hoursForEntirePeriod: values.hoursForEntirePeriod,
            hoursPerMonth: values.hoursPerMonth,
            hoursPerWeek: values.hoursPerWeek
          })
        ) || 0;
      const dateRange = {
        startDate: mapRepliconDateToMidnightUTCString(values.startDate),
        endDate: mapRepliconDateToMidnightUTCString(values.endDate)
      };
      const periodType = values.selectedAllocationPeriod;

      if (periodType === ALLOCATION_PERIOD.ENTIRE_PERIOD) {
        setSmartFitSubmission({
          totalAllocatedHours: hoursPerPeriod,
          allocationDateRange: dateRange,
          periodType
        });
        openSmartFitDialog();
      } else {
        submitAllocation({ dateRange, hoursPerPeriod, periodType });
      }
    },
    [submitAllocation, openSmartFitDialog]
  );

  return {
    onFormSubmit,
    handleSetAllocation,
    isSmartFitOpen,
    smartFitSubmission,
    closeSmartFitDialog
  };
};
